// Tiny markdown renderer for assistant messages. Handles the subset the model
// actually emits in this UI: bare URLs, [text](url) links, **bold**, *italic*,
// and "- " / "* " bullets. Everything else (headings, tables, code blocks,
// nested formatting) is left as escaped text — keeps the surface small enough
// to audit without pulling in marked + DOMPurify.
//
// The CSS sets white-space: pre-wrap on .message, so we leave "\n" alone
// instead of converting to <br>.

// `*` is in here so `**https://x.com/**` peels the bold wrapper off the URL
// instead of baking it into the href; the bold pass picks it up on the rebound.
const URL_TRAIL = /[.,;:!?)\]*]+$/;
// Site-relative paths (`/contact/`) are allowed: owners write knowledge that
// way and they resolve against the host page, i.e. the agent's own site.
// `//host` and `/\host` are protocol-relative to browsers, so they're refused.
const SAFE_LINK = /^(https?:|mailto:|tel:|\/(?![/\\]))/i;

// `siteOrigin`: where site-relative links point when not rendering on the
// agent's own site (the dashboard transcript viewer on app.chatporch.com).
export function renderMarkdown(input: string, siteOrigin?: string): string {
  let s = escapeHtml(input);

  // Stash links first so their injected HTML isn't re-processed by the bold /
  // italic passes — without this, `**https://x.com/**` autolinks to an anchor
  // containing `**`, and the bold regex then chews through the href attribute.
  // Sentinel is plain ASCII the model has no reason to emit verbatim.
  const links: string[] = [];
  const stash = (html: string) => {
    links.push(html);
    return `@@CPLINK${links.length - 1}@@`;
  };

  s = s.replace(
    /\[([^\]\n]+)\]\(([^\s)]+)\)/g,
    (_match, text: string, url: string) => {
      // `url` is already HTML-escaped (escapeHtml above), so it goes into the
      // attribute as-is; escaping again turns `&` into a literal `&amp;`.
      let safe = SAFE_LINK.test(url) ? url : "#";
      if (siteOrigin && safe.startsWith("/")) safe = escapeHtml(siteOrigin) + safe;
      return stash(`<a href="${safe}" target="_blank" rel="noopener noreferrer">${text}</a>`);
    },
  );

  // Bare URL → anchor. Trailing sentence punctuation (and any `*` the model
  // wrapped the URL in for bold) is stripped from the link and re-appended
  // outside, so "see https://foo.com." doesn't capture the period.
  s = s.replace(/https?:\/\/[^\s<>]+/g, (url) => {
    const trailing = url.match(URL_TRAIL)?.[0] ?? "";
    const clean = trailing ? url.slice(0, -trailing.length) : url;
    return stash(`<a href="${clean}" target="_blank" rel="noopener noreferrer">${clean}</a>`) + trailing;
  });

  // Bullet lines. The bare bullet char keeps things flowing under pre-wrap;
  // a real <ul>/<li> would need block layout and gap tweaks for marginal gain.
  s = s.replace(/^[ \t]*[*\-] +/gm, "• ");

  // Bold before italic so ** isn't eaten by the * pass. Both forbid newlines
  // in the match so a stray asterisk doesn't swallow a paragraph.
  s = s.replace(/\*\*([^*\n]+)\*\*/g, "<strong>$1</strong>");
  s = s.replace(/\*([^*\n]+)\*/g, "<em>$1</em>");

  return s.replace(/@@CPLINK(\d+)@@/g, (_m, i: string) => links[Number(i)]!);
}

export function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!,
  );
}
